import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { 
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator
} from '@/components/ui/dropdown-menu';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { 
  ChevronDown, 
  Plus,
  Upload,
  Search, 
  Edit, 
  FileText, 
  DollarSign, 
  Trash2, 
  MoreVertical,
  Eye,
  Settings
} from 'lucide-react';
import { Employee } from '@/types';
import { EmployeeForm } from './EmployeeForm';
import { EmployeeSalaryManager } from './EmployeeSalaryManager';
import { useToast } from '@/hooks/use-toast';
import { 
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { 
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { apiService } from "@/services/api";
import { EmployeePayslipViewer } from '@/components/employees/EmployeePayslipViewer';
import { BulkImportEmployees } from '@/components/employees/BulkImportEmployees';
import { Card, CardContent } from '@/components/ui/card';
import { useAuth } from "@/contexts/AuthContext";

interface EmployeeListProps {
  employees: Employee[];
  onAddEmployee?: (employee: Partial<Employee>) => void;
  onEditEmployee?: (id: string, employee: Partial<Employee>) => void;
  onDeleteEmployee?: (id: string) => void;
  onImportComplete?: () => void;
}

export const EmployeeList = ({ 
  employees,
  onAddEmployee = () => {}, 
  onEditEmployee = () => {},
  onDeleteEmployee = () => {},
  onImportComplete = () => {},
}: EmployeeListProps) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [bulkImportOpen, setBulkImportOpen] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState<Employee | undefined>();
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isSalaryManagerOpen, setIsSalaryManagerOpen] = useState(false);
  const [isPayslipViewerOpen, setIsPayslipViewerOpen] = useState(false);
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [employeeToDelete, setEmployeeToDelete] = useState<Employee | null>(null);
  const navigate = useNavigate();
  const { toast } = useToast();
  const { user } = useAuth();

  // Load employees from localStorage
  const loadEmployeesFromLocalStorage = () => {
    const storedEmployees = JSON.parse(localStorage.getItem("employees") || "[]");
    return storedEmployees;
  };

  // Save employees to localStorage
  const saveEmployeesToLocalStorage = (updatedEmployees: Employee[]) => {
    localStorage.setItem("employees", JSON.stringify(updatedEmployees));
  };

  // Filter employees based on search query
  const filteredEmployees = employees.filter(employee => 
    employee.firstName.toLowerCase().includes(searchQuery.toLowerCase()) ||
    employee.lastName.toLowerCase().includes(searchQuery.toLowerCase()) ||
    employee.employeeId?.toLowerCase().includes(searchQuery.toLowerCase()) 
  
  );

  const [departments, setDepartments] = useState([]);
  const [positions, setPositions] = useState([]);


  useEffect(() => {
    const cid = user?.cid ?? null;
    apiService.getDepartments(cid).then(data => setDepartments(data.data));
    apiService.getPositions(cid).then(data => setPositions(data.data));
  }, [user?.cid]);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'bg-green-100 text-green-800';
      case 'inactive':
        return 'bg-red-100 text-red-800';
      case 'on_leave':
        return 'bg-amber-100 text-amber-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const handleAddNew = () => {
    setSelectedEmployee(undefined);
    setIsFormOpen(true);
  };

  const handleEdit = async (employee: Employee) => {
    try {
      // Fetch full employee data from backend
      const response = await apiService.getEmployee(employee.id);
      // Handle both array and object responses
      let fullEmployeeData;
      if (Array.isArray(response.data)) {
        fullEmployeeData = response.data.length > 0 ? response.data[0] : employee;
      } else if (response.data) {
        fullEmployeeData = response.data;
      } else {
        fullEmployeeData = employee;
      }
      
      // Set the selected employee with full data
      setSelectedEmployee(fullEmployeeData as Employee);

      // Open the EmployeeForm modal
      setIsFormOpen(true);
    } catch (error) {
      console.error('Error fetching employee data:', error);
      toast({
        title: "Error",
        description: "Failed to load employee data. Please try again.",
        variant: "destructive",
      });
      // Fallback to using the employee data from the list
      setSelectedEmployee(employee);
      setIsFormOpen(true);
    }
  };

  const handleSubmit = (data: Partial<Employee>) => {
    // Load employees from localStorage
    const storedEmployees = loadEmployeesFromLocalStorage();

    if (selectedEmployee) {
      // Update the selected employee's data
      const updatedEmployees = storedEmployees.map((emp: Employee) =>
        emp.id === selectedEmployee.id ? { ...emp, ...data } : emp
      );

      // Save updated employees back to localStorage
      saveEmployeesToLocalStorage(updatedEmployees);

      // Call the onEditEmployee callback
      onEditEmployee(selectedEmployee.id, data);

      // Show a success toast
      toast({
        title: "Employee Updated",
        description: `${selectedEmployee.firstName} ${selectedEmployee.lastName}'s information has been updated.`,
      });
    } else {
      // Add a new employee if no employee is selected
      const newEmployee = {
        id: String(Date.now()), // Generate a unique ID
        ...data,
      } as Employee;

      const updatedEmployees = [...storedEmployees, newEmployee];
      saveEmployeesToLocalStorage(updatedEmployees);

      // Call the onAddEmployee callback
      onAddEmployee(newEmployee);

      // Show a success toast
      toast({
        title: "Employee Added",
        description: `${newEmployee.firstName} ${newEmployee.lastName} has been added.`,
      });
    }

    // Close the EmployeeForm modal
    setIsFormOpen(false);
  };

  const handleViewDetails = (employee: Employee) => {
    setSelectedEmployee(employee);
    setIsDetailsOpen(true);
  };

  const handleSalaryDetails = (employee: Employee) => {
    setSelectedEmployee(employee);
    setIsSalaryManagerOpen(true);
  };

  const handleViewPayslip = (employee: Employee) => {
    setSelectedEmployee(employee);
    setIsPayslipViewerOpen(true);
  };

  const handleDelete = (employee: Employee) => {
    setEmployeeToDelete(employee);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = () => {
    if (employeeToDelete) {
      const storedEmployees = loadEmployeesFromLocalStorage();
      const updatedEmployees = storedEmployees.filter((emp: Employee) => emp.id !== employeeToDelete.id);
      saveEmployeesToLocalStorage(updatedEmployees);
      onDeleteEmployee(employeeToDelete.id);

      toast({
        title: "Employee Deleted",
        description: `${employeeToDelete.firstName} ${employeeToDelete.lastName} has been removed.`,
      });

      setEmployeeToDelete(null);
    }
    setDeleteDialogOpen(false);
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="relative w-64">
          <Search className="absolute left-2 top-2.5 h-4 w-4 text-gray-500" />
          <Input
            placeholder="Search employees..."
            className="pl-8"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setBulkImportOpen(true)}>
            <Upload className="h-4 w-4 mr-1" />
            Bulk Import
          </Button>
          <Button className="bg-payroll-primary hover:bg-blue-700" onClick={handleAddNew}>
            <Plus className="h-4 w-4 mr-1" />
            Add Employee
          </Button>
        </div>
      </div>

      <BulkImportEmployees
        open={bulkImportOpen}
        onOpenChange={setBulkImportOpen}
        onImportComplete={onImportComplete}
      />
      
      <div className="bg-white rounded-lg shadow">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Employee</TableHead>
              <TableHead>Position</TableHead>
              <TableHead>Department</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Join Date</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredEmployees.length > 0 ? (
              filteredEmployees.map((employee) => (
                <TableRow key={employee.id}>
                  <TableCell>
                    <div className="flex items-center">
                      <Avatar className="h-8 w-8 mr-2">
                        <AvatarFallback>
                          {employee.firstName[0] + employee.lastName[0]}
                        </AvatarFallback>
                      </Avatar>
                      <div>
                        <Link 
                          to={`/employees/${employee.id}`} 
                          className="font-medium text-blue-600 hover:underline"
                        >
                          {employee.firstName} {employee.lastName}
                        </Link>
                        <p className="text-xs text-gray-500">{employee.employeeId}</p>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>{positions.find(a=>a.id === employee.position_id)?.title}</TableCell>
                  <TableCell>{departments.find(a=>a.id === employee.department_id)?.name}</TableCell>
                  <TableCell>
                    <Badge className={getStatusColor(employee.status)}>
                      {employee.status === 'on_leave' ? 'On Leave' : 
                        employee.status.charAt(0).toUpperCase() + employee.status.slice(1)
                      }
                    </Badge>
                  </TableCell>
                  <TableCell>{new Date(employee.joinDate).toLocaleDateString()}</TableCell>
                  <TableCell className="text-right">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="sm">
                          <MoreVertical className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => handleViewDetails(employee)}>
                          <Eye className="h-4 w-4 mr-2" />
                          View Details
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => handleEdit(employee)}>
                          <Edit className="h-4 w-4 mr-2" />
                          Edit
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => handleSalaryDetails(employee)}>
                          <Settings className="h-4 w-4 mr-2" />
                          Salary Details
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => handleViewPayslip(employee)}>
                          <FileText className="h-4 w-4 mr-2" />
                          View Payslips
                        </DropdownMenuItem>
                      
                        <DropdownMenuSeparator />
                        <DropdownMenuItem className="text-red-600" onClick={() => handleDelete(employee)}>
                          <Trash2 className="h-4 w-4 mr-2" />
                          Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={7} className="text-center py-4">
                  <p className="text-gray-500">No employees found matching your search query.</p>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      <EmployeeForm 
        isOpen={isFormOpen}
        onClose={() => setIsFormOpen(false)}
        onSubmit={handleSubmit}
        employee={selectedEmployee}
      />

      {selectedEmployee && (
        <Dialog open={isSalaryManagerOpen} onOpenChange={setIsSalaryManagerOpen}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Employee Salary Management</DialogTitle>
            </DialogHeader>
            <EmployeeSalaryManager 
              employeeId={selectedEmployee.id}
              employeeName={`${selectedEmployee.firstName} ${selectedEmployee.lastName}`}
            />
          </DialogContent>
        </Dialog>
      )}

      {selectedEmployee && (
        <Dialog open={isPayslipViewerOpen} onOpenChange={setIsPayslipViewerOpen}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>View Payslip</DialogTitle>
            </DialogHeader>
            <EmployeePayslipViewer 
              employeeId={selectedEmployee.id}
              employeeName={`${selectedEmployee.firstName} ${selectedEmployee.lastName}`}
            />
          </DialogContent>
        </Dialog>
      )}

      {selectedEmployee && (
        <Dialog open={isDetailsOpen} onOpenChange={setIsDetailsOpen}>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Employee Details</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div className="flex items-center gap-4">
                <Avatar className="h-12 w-12">
                  <AvatarFallback>
                    {selectedEmployee.firstName?.[0]}{selectedEmployee.lastName?.[0]}
                  </AvatarFallback>
                </Avatar>
                <div>
                  <div className="text-xl font-semibold">{selectedEmployee.firstName} {selectedEmployee.lastName}</div>
                  <div className="text-sm text-gray-600">Employee ID: {selectedEmployee.employeeId}</div>
                  <Badge className="mt-1">{selectedEmployee.status === 'on_leave' ? 'On Leave' : (selectedEmployee.status || 'active')}</Badge>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Card>
                  <CardContent className="p-4 space-y-2">
                    <div>
                      <div className="text-xs text-gray-500">Position</div>
                      <div className="font-medium">{positions.find(a=>a.id === selectedEmployee.position_id)?.title || '—'}</div>
                    </div>
                    <div>
                      <div className="text-xs text-gray-500">Department</div>
                      <div className="font-medium">{departments.find(a=>a.id === selectedEmployee.department_id)?.name || '—'}</div>
                    </div>
                    <div>
                      <div className="text-xs text-gray-500">Division</div>
                      <div className="font-medium">{selectedEmployee.division || '—'}</div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <div className="text-xs text-gray-500">Join Date</div>
                        <div className="font-medium">{selectedEmployee.joinDate ? new Date(selectedEmployee.joinDate).toLocaleDateString() : '—'}</div>
                      </div>
                      <div>
                        <div className="text-xs text-gray-500">Engagement Date</div>
                        <div className="font-medium">{selectedEmployee.engagementDate ? new Date(selectedEmployee.engagementDate).toLocaleDateString() : '—'}</div>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="p-4 space-y-2">
                    <div>
                      <div className="text-xs text-gray-500">Email</div>
                      <div className="font-medium break-all">{selectedEmployee.email || '—'}</div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <div className="text-xs text-gray-500">Phone</div>
                        <div className="font-medium">{selectedEmployee.phone || '—'}</div>
                      </div>
                      <div>
                        <div className="text-xs text-gray-500">Cell</div>
                        <div className="font-medium">{selectedEmployee.cellNumber || '—'}</div>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <div className="text-xs text-gray-500">NRC</div>
                        <div className="font-medium">{selectedEmployee.nrc || '—'}</div>
                      </div>
                      <div>
                        <div className="text-xs text-gray-500">TPIN</div>
                        <div className="font-medium">{selectedEmployee.taxId || '—'}</div>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <div className="text-xs text-gray-500">NHIMA Number</div>
                        <div className="font-medium">{selectedEmployee.nhimaNumber || '—'}</div>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="p-4 space-y-2">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <div className="text-xs text-gray-500">Bank Name</div>
                        <div className="font-medium">{selectedEmployee.bankName || '—'}</div>
                      </div>
                      <div>
                        <div className="text-xs text-gray-500">Branch</div>
                        <div className="font-medium">{selectedEmployee.bankBranch || '—'}</div>
                      </div>
                    </div>
                    <div>
                      <div className="text-xs text-gray-500">Account</div>
                      <div className="font-medium break-all">{selectedEmployee.bankAccount || '—'}</div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <div className="text-xs text-gray-500">Social Security No.</div>
                        <div className="font-medium">{selectedEmployee.socialSecurityNo || '—'}</div>
                      </div>
                      <div>
                        <div className="text-xs text-gray-500">Union</div>
                        <div className="font-medium">{selectedEmployee.unionType || '—'}</div>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="p-4 space-y-2">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <div className="text-xs text-gray-500">Gender</div>
                        <div className="font-medium capitalize">{selectedEmployee.gender || '—'}</div>
                      </div>
                      <div>
                        <div className="text-xs text-gray-500">DOB</div>
                        <div className="font-medium">{selectedEmployee.dateOfBirth ? new Date(selectedEmployee.dateOfBirth).toLocaleDateString() : '—'}</div>
                      </div>
                    </div>
                    <div>
                      <div className="text-xs text-gray-500">Man Number</div>
                      <div className="font-medium">{selectedEmployee.manNumber || '—'}</div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete {employeeToDelete?.firstName} {employeeToDelete?.lastName}'s record.
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-red-600 hover:bg-red-700">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
